"use client";
import { useState, useCallback, useMemo } from "react";
import { CcofPatientSearchResult } from "lib/api-types/schemas";
import { usePatientSearch } from "@/features/patient/queries/usePatientSearch";
import { useTenantRouteContext } from "@/components/app-shell/TenantRouteContext";
import {
  CommandPaletteProps,
  CommandPaletteResult,
  CommandPaletteResultGroup,
} from "./types";

function toPatientResult(
  patient: CcofPatientSearchResult,
): CommandPaletteResult {
  const firstName = patient.firstName ?? "";
  const lastName = patient.lastName ?? "";
  const mrn = patient.mrn ?? "";
  const phone = patient.phoneNumber ?? "";
  const dob = patient.dateOfBirth ?? "";

  return {
    id: `patient-${patient.patientId}`,
    kind: "patient" as const,
    label: [firstName, lastName].filter(Boolean).join(" "),
    sublabel: mrn || undefined,
    patient: {
      patientId: patient.patientId ?? "",
      firstName,
      lastName,
      mrn,
      phone,
      dob,
      card:
        patient.cardBrand && patient.cardLast4
          ? { brand: patient.cardBrand, last4: patient.cardLast4 }
          : null,
    },
  };
}

export function useCommandPaletteSearch({
  onClose,
  onNavigateToTab,
  onSelectPatient,
}: Omit<CommandPaletteProps, "isOpen">) {
  const { tenantId } = useTenantRouteContext();
  const [submittedQuery, setSubmittedQuery] = useState("");
  const formattedQuery = submittedQuery.trim().toLowerCase();

  const {
    items: patientResults,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = usePatientSearch(tenantId, formattedQuery);

  const results: CommandPaletteResultGroup[] = useMemo(() => {
    if (!formattedQuery) {
      return [{ group: "Patients", items: [] }];
    }
    return [{ group: "Patients", items: patientResults.map(toPatientResult) }];
  }, [patientResults, formattedQuery]);

  const flatResults = useMemo(
    () => results.flatMap((group) => group.items),
    [results],
  );

  const handleSearchSubmit = useCallback((value: string) => {
    setSubmittedQuery(value.trim());
  }, []);

  const handleClear = useCallback(() => {
    setSubmittedQuery("");
  }, []);

  const handleSelect = useCallback(
    (result: CommandPaletteResult) => {
      if (result.kind === "patient" && result.patient) {
        onSelectPatient(result.patient);
        onClose();
      } else if (result.kind === "tab" && result.tab) {
        onNavigateToTab(result.tab);
        onClose();
      }
    },
    [onSelectPatient, onNavigateToTab, onClose],
  );

  return {
    submittedQuery,
    results,
    flatResults,
    handleSelect,
    handleSearchSubmit,
    handleClear,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  };
}
